// api/user.js
import { useState, useEffect, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import axios from "utils/axios";
import { authService } from "services/authService";

export const useGetUser = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const lastReduxUserId = useRef(null);

  const reduxUserId = useSelector((state) => state.auth.user?.id ?? null);
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const logoutTriggered = useRef(false);

  const fetchUser = useCallback(async ({ soft = false } = {}) => {
    try {
      if (!soft) setLoading(true);
      setError(null);
      const response = await axios.get(`/api/users/personal-info/`);
      setData(response.data);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch user data");
      return null;
    } finally {
      if (!soft) setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // Refetch only when the authenticated user identity changes (login / switch account)
  useEffect(() => {
    if (!reduxUserId) {
      lastReduxUserId.current = null;
      logoutTriggered.current = false;
      return;
    }
    if (lastReduxUserId.current === reduxUserId) return;
    lastReduxUserId.current = reduxUserId;
    fetchUser({ soft: true });
  }, [reduxUserId, fetchUser]);

  // Stale session (e.g. DB reset): token/user in storage but profile no longer exists.
  useEffect(() => {
    if (loading || data || !isLoggedIn || logoutTriggered.current) return;
    logoutTriggered.current = true;
    authService.logout();
  }, [loading, data, isLoggedIn]);

  return {
    user: data,
    userLoading: loading,
    userError: error,
    refetchUser: () => fetchUser({ soft: true }),
  };
};

export const useGetProfessional = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const lastReduxUserId = useRef(null);

  const reduxUserId = useSelector((state) => state.auth.user?.id ?? null);
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const logoutTriggered = useRef(false);

  const fetchUserProfessional = useCallback(async ({ soft = false } = {}) => {
    try {
      if (!soft) setLoading(true);
      setError(null);
      const response = await axios.get(`/api/users/professional-info/`);
      setData(response.data);
      return response.data;
    } catch (err) {
      setError(
        err.response?.data?.message || "Failed to fetch professional data",
      );
      console.error("Error fetching user:", err);
      return null;
    } finally {
      if (!soft) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUserProfessional();
  }, [fetchUserProfessional]);

  useEffect(() => {
    if (!reduxUserId) {
      lastReduxUserId.current = null;
      logoutTriggered.current = false;
      return;
    }
    if (lastReduxUserId.current === reduxUserId) return;
    lastReduxUserId.current = reduxUserId;
    fetchUserProfessional({ soft: true });
  }, [reduxUserId, fetchUserProfessional]);

  useEffect(() => {
    if (loading || data || !isLoggedIn || logoutTriggered.current) return;
    logoutTriggered.current = true;
    authService.logout();
  }, [loading, data, isLoggedIn]);

  return {
    professional: data,
    professionalLoading: loading,
    professionalError: error,
    refetchProfessional: () => fetchUserProfessional({ soft: true }),
  };
};
